import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatBytes, formatRelative, formatWhen, lifecycleLabel, machineDisplayName } from "@/lib/format";
import { ApiClientError, type AdminCabinet, type Machine } from "@/lib/types";
import { AuthStatePanels } from "@/components/AuthState";
import { Button } from "@/components/Button";
import { ButtonLink } from "@/components/ButtonLink";
import { ConfirmSheet } from "@/components/ConfirmSheet";
import { Field, TextArea, TextInput } from "@/components/Field";
import { ClipboardList, Cpu, Landmark, UserRound, Wrench } from "@/components/icons";
import { LoadList } from "@/components/LoadList";
import { StatePanel } from "@/components/StatePanel";
import { useLocale } from "@/locale/LocaleContext";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";
import styles from "./admin.module.css";

const disconnectedKeys = [
  "save",
  "restore",
  "export",
  "delete",
  "payment",
  "icloud",
  "mac_assignment",
  "mail",
  "push",
  "operator_roles",
  "platform_settings",
] as const;

const parkOrder = [
  "display_name",
  "model",
  "lifecycle",
  "health",
  "last_seen_at",
  "mode",
  "rentable",
  "ram_bytes",
  "storage_bytes",
  "id",
] as const;

const byteFields = new Set(["ram_bytes", "storage_bytes", "memory_used_bytes", "memory_free_bytes", "disk_free_bytes"]);

const auditFields = ["actor", "tenant", "resource", "request_id", "outcome"] as const;

type Sample = { at: string; value: number };

function isSeries(value: unknown): value is Sample[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every((item) => {
    if (!item || typeof item !== "object") return false;
    const row = item as Record<string, unknown>;
    return typeof row.value === "number" && Number.isFinite(row.value) && typeof row.at === "string" && row.at.length > 0;
  });
}

function fill(template: string, field: string) {
  return template.replaceAll("{field}", field);
}

function recordOf(value: object): Record<string, unknown> {
  return value as unknown as Record<string, unknown>;
}

function orderedKeys(record: Record<string, unknown>, preferred: readonly string[]) {
  const present = preferred.filter((key) => Object.prototype.hasOwnProperty.call(record, key));
  const rest = Object.keys(record)
    .filter((key) => !present.includes(key) && !isSeries(record[key]))
    .sort();
  return [...present, ...rest];
}

function SeriesChart({ field, points }: { field: string; points: Sample[] }) {
  const { locale, m } = useLocale();
  const scale = Math.max(...points.map((point) => Math.abs(point.value)), 1);
  return (
    <figure className={styles.chart}>
      <figcaption>
        {m.admin.sourceCabinet} · machines[].{field}
      </figcaption>
      <div className={styles.bars} aria-label={field}>
        {points.map((point, index) => (
          <div key={`${point.at}-${index}`} className={styles.barCol} title={`${point.at} ${point.value}`}>
            <span className={styles.barTrack}>
              <span className={styles.bar} style={{ height: `${(Math.abs(point.value) / scale) * 100}%` }} />
            </span>
            <span className={styles.barValue}>{new Intl.NumberFormat(locale).format(point.value)}</span>
          </div>
        ))}
      </div>
    </figure>
  );
}

export function AdminCabinetPage() {
  const { m, locale } = useLocale();
  const [data, setData] = useState<AdminCabinet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [refused, setRefused] = useState(false);
  const [target, setTarget] = useState<Machine | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [serial, setSerial] = useState("");
  const [purpose, setPurpose] = useState<"dedicated-test" | "rental">("dedicated-test");
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const labels: Record<(typeof disconnectedKeys)[number], string> = {
    save: m.admin.save,
    restore: m.admin.restore,
    export: m.admin.export,
    delete: m.admin.delete,
    payment: m.admin.payment,
    icloud: m.admin.icloud,
    mac_assignment: m.admin.assignment,
    mail: m.admin.mail,
    push: m.admin.push,
    operator_roles: m.admin.roles,
    platform_settings: m.admin.settings,
  };

  async function load() {
    setLoading(true);
    try {
      const cabinet = await api.adminCabinet();
      setData(cabinet);
      setError(null);
      setRefused(false);
    } catch (e) {
      const err = e instanceof ApiClientError ? e : new ApiClientError(0, "error", m.states.error);
      setRefused(err.code === "INTERNAL_NETWORK");
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!data) return;
    setSelectedId((current) =>
      current && data.machines.some((item) => item.id === current) ? current : (data.machines[0]?.id ?? null),
    );
  }, [data]);

  async function onMaintenance() {
    if (!target || reason.trim().length < 8) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.setMaintenance(target.id, { enabled, reason: reason.trim() });
      setTarget(null);
      setReason("");
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : m.operator.actionFailed);
    } finally {
      setBusy(false);
    }
  }

  async function onEnroll() {
    setEnrolling(true);
    setEnrollError(null);
    setToken(null);
    try {
      const receipt = await api.enrollMachine({
        display_name: name.trim(),
        serial: serial.trim().toUpperCase(),
        purpose,
      });
      setToken(receipt.connector_token);
      setName("");
      setSerial("");
      await load();
    } catch (e) {
      setEnrollError(e instanceof Error ? e.message : m.admin.enrollFailed);
    } finally {
      setEnrolling(false);
    }
  }

  function factLabel(key: string) {
    if (key === "ram_bytes") return `${m.admin.installedMemory} · ram_bytes`;
    if (key === "storage_bytes") return `${m.admin.installedStorage} · storage_bytes`;
    return key;
  }

  function factValue(machine: Machine, key: string, value: unknown) {
    if (value == null) return fill(m.admin.fieldNull, key);
    if (byteFields.has(key) && typeof value === "number") return formatBytes(value, locale);
    if (key === "display_name") return machineDisplayName(m, machine);
    if (key === "lifecycle" || key === "health") return lifecycleLabel(m, String(value));
    if (key === "last_seen_at" && typeof value === "string") return formatRelative(value, locale);
    if (typeof value === "number") return new Intl.NumberFormat(locale).format(value);
    if (typeof value === "string" || typeof value === "boolean") return String(value);
    return fill(m.admin.fieldNull, key);
  }

  if (refused) {
    return <StatePanel kind="forbidden" title={m.admin.refusedTitle} message={m.admin.refusedBody} />;
  }

  if (loading) return <LoadList title={m.common.loading} rows={4} />;

  const selected = data?.machines.find((item) => item.id === selectedId) ?? null;

  return (
    <AuthStatePanels loading={false} error={error}>
      {!data ? (
        <StatePanel kind="empty" title={m.states.empty} />
      ) : (
        <div className={styles.page}>
          <h1 className={shell.pageTitle}>{m.admin.title}</h1>
          <p className={shell.pageLead}>{m.admin.lead}</p>
          <p className={styles.source}>{m.admin.sourceCabinet} · network.enforced · network.ranges</p>
          <p className={ui.meta}>{m.admin.network}</p>
          <p className={`mono ${ui.meta}`}>{String(data.network.enforced)}</p>
          <p className={`mono ${ui.meta} ${styles.ranges}`}>{data.network.ranges.join(" · ")}</p>
          <p className={ui.meta}>{m.admin.rangesNote}</p>

          <section id="park" className={styles.section}>
            <h2 className={`display ${ui.sectionHead}`}>
              <Cpu size={18} aria-hidden />
              {m.admin.park}
            </h2>
            <p className={styles.source}>
              {m.admin.sourceCabinet} · {m.admin.sourceMachines}
            </p>
            {data.machines.length === 0 ? <p className={ui.meta}>{m.admin.noMachines}</p> : null}
            <div className={styles.picker} role="radiogroup" aria-label={m.admin.park}>
              {data.machines.map((machine, index) => (
                <button
                  key={machine.id}
                  type="button"
                  role="radio"
                  aria-checked={machine.id === selectedId}
                  className={styles.pick}
                  onClick={() => setSelectedId(machine.id)}
                >
                  <span className={styles.pickIndex}>{index + 1}</span>
                  <span className={styles.pickBody}>
                    <span>{machineDisplayName(m, machine)}</span>
                    <span className={styles.source}>
                      {lifecycleLabel(m, machine.lifecycle)} · {lifecycleLabel(m, machine.health)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
            {selected ? (
              <div>
                <p className={styles.source}>
                  {m.admin.sourceCabinet} · {m.admin.sourceMachines}[]
                </p>
                <div className={styles.facts}>
                  {orderedKeys(recordOf(selected), parkOrder).map((key) => (
                    <div key={key}>
                      <span className={styles.source}>{factLabel(key)}</span>
                      <strong>{factValue(selected, key, recordOf(selected)[key])}</strong>
                    </div>
                  ))}
                </div>
                <div className={styles.charts}>
                  {Object.entries(recordOf(selected))
                    .filter((entry): entry is [string, Sample[]] => isSeries(entry[1]))
                    .map(([field, points]) => (
                      <SeriesChart key={field} field={field} points={points} />
                    ))}
                </div>
                {Object.prototype.hasOwnProperty.call(selected, "serial") ? null : (
                  <p className={styles.absent}>machines[].serial</p>
                )}
                {data.controls.maintenance ? (
                  <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setEnabled(selected.lifecycle !== "maintenance");
                        setReason("");
                        setActionError(null);
                        setTarget(selected);
                      }}
                    >
                      {selected.lifecycle === "maintenance" ? m.operator.maintenanceOff : m.operator.maintenanceOn}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

          <section id="counts" className={styles.section}>
            <h2 className={`display ${ui.sectionHead}`}>
              <Landmark size={18} aria-hidden />
              {m.admin.counts}
            </h2>
            <p className={styles.source}>
              {m.admin.sourceCabinet} · {m.admin.sourceTenants}
            </p>
            <p className={styles.figure}>{data.counts.tenants}</p>
            <p className={styles.source}>
              {m.admin.sourceCabinet} · {m.admin.sourceUsers}
            </p>
            <p className={styles.figure}>{data.counts.users}</p>
            <p className={ui.meta}>{m.admin.usersNote}</p>
            <p className={styles.source}>
              {m.admin.sourceCabinet} · {m.admin.sourceTasks}
            </p>
            <p className={styles.figure}>{data.counts.tasks}</p>
          </section>

          <section id="audit" className={styles.section}>
            <h2 className={`display ${ui.sectionHead}`}>
              <ClipboardList size={18} aria-hidden />
              {m.admin.audit}
            </h2>
            <p className={styles.source}>{m.admin.sourceCabinet} · recent_events</p>
            {data.recent_events.length === 0 ? <p className={ui.meta}>{m.operator.noEvents}</p> : null}
            {data.recent_events.map((event) => {
              const record = recordOf(event);
              const keys = orderedKeys(record, ["action", "created_at", "id"]);
              return (
                <div key={event.id} className={styles.line}>
                  <div>
                    {keys.map((key) => (
                      <div key={key}>
                        <span className={styles.source}>recent_events[].{key}</span>
                        <div>
                          {key === "created_at" && typeof record[key] === "string"
                            ? formatWhen(String(record[key]), locale)
                            : record[key] == null
                              ? fill(m.admin.fieldNull, key)
                              : String(record[key])}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {auditFields
              .filter((field) => data.recent_events.every((event) => !Object.prototype.hasOwnProperty.call(event, field)))
              .map((field) => (
                <p key={field} className={styles.absent}>
                  recent_events[].{field}
                </p>
              ))}
          </section>

          <section id="settings" className={styles.section}>
            <h2 className={`display ${ui.sectionHead}`}>
              <Wrench size={18} aria-hidden />
              {m.admin.settings}
            </h2>
            <p className={styles.source}>{m.admin.sourceCabinet} · disconnected</p>
            <p className={styles.absent}>platform_settings</p>
          </section>

          <section id="roles" className={styles.section}>
            <h2 className={`display ${ui.sectionHead}`}>
              <UserRound size={18} aria-hidden />
              {m.admin.roles}
            </h2>
            <p className={styles.source}>{m.admin.sourceCabinet} · disconnected</p>
            <p className={styles.absent}>operator_roles</p>
          </section>

          {data.controls.workspace_review ? (
            <section className={styles.section}>
              <h2 className="display" style={{ fontSize: "1.1rem" }}>
                controls.workspace_review
              </h2>
              <p className={styles.source}>{m.admin.sourceCabinet} · controls.workspace_review</p>
              <p className={ui.meta}>{m.admin.reviewLead}</p>
              <div className={ui.actions} style={{ justifyContent: "flex-start" }}>
                <ButtonLink to="/operator/workspace-requests" variant="secondary">
                  {m.operatorWorkspace.openQueue}
                </ButtonLink>
              </div>
            </section>
          ) : null}

          {data.controls.enrollment ? (
            <section className={styles.section}>
              <h2 className="display" style={{ fontSize: "1.1rem" }}>
                controls.enrollment
              </h2>
              <p className={styles.source}>{m.admin.sourceCabinet} · controls.enrollment</p>
              <p className={ui.meta}>{m.admin.enrollLead}</p>
              <Field label={m.admin.enrollName} htmlFor="admin-enroll-name">
                <TextInput id="admin-enroll-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
              </Field>
              <Field label={m.admin.serial} htmlFor="admin-enroll-serial">
                <TextInput id="admin-enroll-serial" value={serial} onChange={(e) => setSerial(e.target.value)} autoComplete="off" spellCheck={false} />
              </Field>
              <Field label={m.admin.enrollPurpose} htmlFor="admin-enroll-purpose">
                <select id="admin-enroll-purpose" className={ui.input} value={purpose} onChange={(e) => setPurpose(e.target.value as "dedicated-test" | "rental")}>
                  <option value="dedicated-test">{m.admin.purposeTest}</option>
                  <option value="rental">{m.admin.purposeRental}</option>
                </select>
              </Field>
              <Button
                variant="secondary"
                disabled={enrolling || name.trim().length < 2 || !/^[A-Z0-9]{8,20}$/i.test(serial.trim())}
                onClick={() => void onEnroll()}
              >
                {m.admin.enrollSubmit}
              </Button>
              {token ? (
                <p className={`mono ${styles.token}`}>
                  {m.admin.tokenOnce} {token}
                </p>
              ) : null}
              {enrollError ? <p className={ui.fieldError}>{enrollError}</p> : null}
            </section>
          ) : null}

          <section className={styles.section}>
            <h2 className="display" style={{ fontSize: "1.1rem" }}>
              disconnected
            </h2>
            <p className={styles.source}>{m.admin.sourceCabinet} · disconnected</p>
            <p className={ui.meta}>{m.admin.notConnectedLead}</p>
            {data.disconnected.map((key) => (
              <div key={key} className={styles.line}>
                <div>{key in labels ? labels[key as keyof typeof labels] : key}</div>
                <div className={styles.value}>{m.admin.notConnected}</div>
              </div>
            ))}
          </section>

          <ConfirmSheet
            open={Boolean(target)}
            title={m.operator.confirmTitle}
            confirmLabel={m.operator.confirmAction}
            confirmDisabled={reason.trim().length < 8}
            busy={busy}
            onClose={() => setTarget(null)}
            onConfirm={() => void onMaintenance()}
          >
            <p>{m.operator.confirmBody}</p>
            {target ? <p className={ui.meta}>{machineDisplayName(m, target)}</p> : null}
            <Field label={m.operator.reason} htmlFor="admin-maint-reason" hint={m.operator.reasonRequired}>
              <TextArea id="admin-maint-reason" className="resize-none" style={{ resize: "none" }} value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            {actionError ? <p className={ui.fieldError}>{actionError}</p> : null}
          </ConfirmSheet>
        </div>
      )}
    </AuthStatePanels>
  );
}
